/**
 * The glass holder (claude-docs/2026-10-30-venus-full-line-design.md, group
 * 5): Venus `GL30` / `GL36`, "Wall - Glass Holder - 30"W x 1-1/2"H x 12"D" —
 * stemware rails hung under a wall cabinet as wide, the glasses hanging by
 * their feet between them (catalog p.16). The one rule the fill, the write
 * boundary, the panel, the drawing and the bill read.
 *
 * An add-on under a cabinet, like a base's roll-out trays (my call): an upper
 * bay's record asks for one (`glassHolder: true`) and the fill stamps it on
 * the cabinet where it fits (`stampGlassHolders`) — a 30" or 36" wall
 * cabinet hanging over the counter. A generic line offers the same sizes, at
 * the system figure.
 *
 * Pure; metres.
 */
const { overridesForRun } = require('./bayOverrides');
const { isRack } = require('./racks');

const IN = 0.0254;
const TOL_IN = 1 / 16;

const GLASS_HOLDER = Object.freeze({
  widthsIn: Object.freeze([30, 36]),
  heightIn: 1.5,
  depthIn: 12,
  // the rails: each this wide, one every `railPitchIn` across
  railWidthIn: 0.75,
  railPitchIn: 4,
});

/**
 * What a glass holder costs on a generic line, each — the system figure,
 * set near Venus's cheapest colour (GL30 $77.28, GL36 $88.48 in Houston
 * Frost on the 2025-11-01 list).
 */
const GENERIC_GLASS_HOLDER_FIGURES = Object.freeze({ 30: 77, 36: 88 });

const said = (m) => `${Math.round((m / IN) * 8) / 8}"`;

const isUpper = (o) =>
  o?.type === 'upper-cabinet' || o?.category?.['upper-cabinet'] === true;

/**
 * Why this cabinet cannot carry a glass holder, or null when it can: a wall
 * cabinet with an underside over the counter — not the fridge's, a stacked
 * one, a corner, a filler or anything open or standing on the counter — 30"
 * or 36" wide.
 */
function glassHolderReason(cabinet) {
  if (!isUpper(cabinet)) return 'A glass holder hangs under a wall cabinet';
  const c = cabinet.category || {};
  if (
    c.filler ||
    c.stack ||
    c.blindCorner ||
    c.cornerPiece ||
    c.floatingShelves ||
    c.endShelf ||
    c.openShelf ||
    c.wallDrawers ||
    c.valanceBay ||
    c.microwaveShelf ||
    c.angledEnd ||
    c.rackCompanion ||
    isRack(cabinet) ||
    cabinet.label === 'Over-Fridge Cabinet'
  )
    return 'A glass holder hangs under a wall cabinet over the counter';
  const w = cabinet.dimensions?.x;
  if (!(w > 0)) return null;
  if (GLASS_HOLDER.widthsIn.some((x) => Math.abs(x - w / IN) <= TOL_IN))
    return null;
  return `A glass holder is 30" or 36" wide: this cabinet is ${said(w)}`;
}

/** The record that governs a built upper, or null. */
const recordOf = (run, o) =>
  o?.overrideId
    ? overridesForRun(run || {}).find((r) => r.id === o.overrideId) || null
    : null;

/**
 * Each upper whose record asks for a glass holder carries `glassHolder:
 * true` where one fits, and none otherwise — so a re-fit that changes its
 * width takes one away rather than billing a holder no size fits.
 */
function stampGlassHolders(run, uppers) {
  for (const o of uppers) {
    if (recordOf(run, o)?.glassHolder === true && !glassHolderReason(o))
      o.glassHolder = true;
    else delete o.glassHolder;
  }
}

/**
 * A glass holder's rails as seen from the front, under a cabinet `width`
 * wide whose bottom is at `bottomY` (the layout's centred metres): each
 * `{ name, x, y, width, height }`, running the holder's depth front to back.
 */
function glassHolderRails(width, bottomY) {
  const railW = GLASS_HOLDER.railWidthIn * IN;
  const pitch = GLASS_HOLDER.railPitchIn * IN;
  const h = GLASS_HOLDER.heightIn * IN;
  const count = Math.max(2, Math.floor((width - railW) / pitch) + 1);
  const span = (count - 1) * pitch;
  return Array.from({ length: count }, (_, i) => ({
    name: `glass-holder-rail-${i}`,
    x: -span / 2 + i * pitch,
    y: bottomY - h / 2,
    width: railW,
    height: h,
  }));
}

module.exports = {
  GLASS_HOLDER,
  GENERIC_GLASS_HOLDER_FIGURES,
  glassHolderReason,
  stampGlassHolders,
  glassHolderRails,
};
